import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";
import OutsideClickHandler from "react-outside-click-handler";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import axios from "axios";
import { path_url } from "../../Config/config";
import {
  FaColumns,
  FaCopy,
  FaFileCsv,
  FaFilePdf,
  FaPrint,
  FaSearch,
  FaTasks,
  FaPlus,
} from "react-icons/fa";
import "../../Style/DataTable.css";

/* ─── Status Badge Helper ─── */
const getStatusClass = (status = "") => {
  const s = status.toUpperCase().trim();
  if (s === "OPEN") return "status-open";
  if (s === "COMPLETED" || s === "CLOSED") return "status-qualified";
  if (s.includes("PROGRESS")) return "status-in-progress";
  if (s.includes("CANCEL")) return "status-disqualified";
  if (s.includes("PENDING") || s.includes("HOLD")) return "status-new-opportunity";
  return "status-default";
};

const StatusBadge = ({ value }) =>
  value ? (
    <span className={`status-badge ${getStatusClass(value)}`}>{value}</span>
  ) : (
    <span className="status-badge status-default">—</span>
  );

/* ────────────────────────────────────────────────────────────── */
const TaskDashboardTable = ({ tasklist = [], filterKeyword = "", onRefresh }) => {
  const [taskList, setTaskList] = useState(Array.isArray(tasklist) ? tasklist : []);
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState(filterKeyword || "");
  const [colDropOpen, setColDropOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState({
    Subject: true,
    Status: true,
    ID: true,
    "Created By": true,
    Priority: true,
    "Due Date": true,
    "Read Status": true,
  });
  const [userMap, setUserMap] = useState({});
  const navigate = useNavigate();

  // Fetch users to map UUIDs to Names
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const adminId = localStorage.getItem("adminId");
        const token = localStorage.getItem("accessToken");
        if (!adminId || !token) return;
        const res = await axios.get(`${path_url}/users`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { adminId },
        });
        const users = Array.isArray(res.data) ? res.data : res.data?.content || res.data?.data || [];
        const mapping = {};
        users.forEach(u => {
          if (u.id) {
            mapping[u.id] = u.name || u.username || u.email || u.id;
          }
        });
        setUserMap(mapping);
      } catch (err) {
        console.error("Failed to fetch users for mapping", err);
      }
    };
    fetchUsers();
  }, []);

  // Keep local list in sync if parent's tasklist prop changes
  useEffect(() => {
    setTaskList(Array.isArray(tasklist) ? tasklist : []);
  }, [tasklist]);

  // Sync search filter when parent passes a filterKeyword
  useEffect(() => {
    if (typeof filterKeyword === "string") {
      setSearchText(filterKeyword);
    }
  }, [filterKeyword]);

  // Filter tasks based on search text across all fields
  useEffect(() => {
    if (!searchText.trim()) {
      setFilteredData(taskList);
      return;
    }
    const lowerSearch = searchText.toLowerCase();
    setFilteredData(
      taskList.filter((item) =>
        Object.values(item || {}).some((value) =>
          String(value || "").toLowerCase().includes(lowerSearch)
        )
      )
    );
  }, [searchText, taskList]);

  // Column definitions with robust field fallbacks
  const columns = [
    {
      name: "Subject",
      selector: (row) => row.subject || row.Subject || "—",
      sortable: true,
      cell: (row) => (
        <span
          style={{
            fontWeight: 600,
            color: "#0d9488",
            cursor: "pointer",
          }}
        >
          {row.subject || row.Subject || "—"}
        </span>
      ),
    },
    {
      name: "Status",
      selector: (row) => row.status || row.Status || "—",
      sortable: true,
      cell: (row) => {
        const s = (row.status || row.Status || "").toLowerCase();
        let bg = "#64748b";
        if (s.includes("completed") || s.includes("closed") || s.includes("qualified")) {
          bg = "#16a34a";
        } else if (s.includes("progress") || s.includes("working")) {
          bg = "#ea580c";
        } else if (s.includes("open") || s.includes("new")) {
          bg = "#0284c7";
        } else if (s.includes("hold") || s.includes("pending") || s.includes("wait")) {
          bg = "#ca8a04";
        } else if (s.includes("cancel") || s.includes("disqual")) {
          bg = "#dc2626";
        }
        return (
          <span
            style={{
              backgroundColor: bg,
              color: "#ffffff",
              padding: "4px 10px",
              borderRadius: "12px",
              fontSize: "12px",
              fontWeight: 500,
              display: "inline-block",
            }}
          >
            {row.status || row.Status || "—"}
          </span>
        );
      },
    },
    {
      name: "ID",
      selector: (row) => row.id || row.Id || "—",
      sortable: true,
      width: "90px",
    },
    {
      name: "Created By",
      selector: (row) => {
        const rawId = row.createdBy || row.createdby || row.useradminid;
        return userMap[rawId] || rawId || "—";
      },
      sortable: true,
    },
    {
      name: "Priority",
      selector: (row) => row.priority || row.Priority || "—",
      sortable: true,
      cell: (row) => {
        const p = (row.priority || row.Priority || "").toLowerCase();
        let color = "#16a34a";
        if (p.includes("high") || p.includes("critical") || p.includes("emergency")) {
          color = "#dc2626";
        } else if (p.includes("medium") || p.includes("normal")) {
          color = "#d97706";
        }
        return (
          <span style={{ fontWeight: 600, color }}>
            {row.priority || row.Priority || "—"}
          </span>
        );
      },
    },
    {
      name: "Due Date",
      selector: (row) => {
        const d = row.duedate || row.dueDate || row.due_date;
        if (!d) return "—";
        try {
          return String(d).split("T")[0];
        } catch {
          return String(d);
        }
      },
      sortable: true,
    },
    {
      name: "Read Status",
      selector: (row) => row.readstatus || row.readStatus || row.read_status || "—",
      sortable: true,
      cell: (row) => <StatusBadge value={row.status} />,
    },
  ];

  const visibleColumns = columns.filter((col) => selectedColumns[col.name]);

  // Copy table data to clipboard
  const copyToClipboard = () => {
    if (!filteredData || filteredData.length === 0) {
      toast.info("No task data to copy.");
      return;
    }
    const headers = visibleColumns.map((col) => col.name).join("\t");
    const rows = filteredData
      .map((row) =>
        visibleColumns
          .map((col) => {
            const val = col.selector(row);
            return val !== undefined && val !== null ? String(val) : "";
          })
          .join("\t")
      )
      .join("\n");
    navigator.clipboard.writeText(`${headers}\n${rows}`);
    toast.success("Task data copied to clipboard!");
  };

  // Export CSV
  const exportCSV = () => {
    if (!filteredData || filteredData.length === 0) {
      toast.info("No task data to export.");
      return;
    }
    const csvContent =
      "data:text/csv;charset=utf-8," +
      [
        visibleColumns.map((col) => `"${col.name}"`).join(","),
        ...filteredData.map((row) =>
          visibleColumns
            .map((col) => {
              const val = col.selector(row);
              const cleanVal = (val !== undefined && val !== null ? String(val) : "").replace(/"/g, '""');
              return `"${cleanVal}"`;
            })
            .join(",")
        ),
      ].join("\n");

    // FIXED: Use csvContent instead of undefined encodedUri
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", "tasks.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Tasks exported to CSV successfully!");
  };

  // Export PDF
  const exportPDF = () => {
    if (!filteredData || filteredData.length === 0) {
      toast.info("No task data to export.");
      return;
    }
    try {
      const doc = new jsPDF();
      doc.setFontSize(14);
      doc.text("Task Table Report", 14, 14);
      doc.autoTable({
        startY: 22,
        head: [visibleColumns.map((col) => col.name)],
        body: filteredData.map((row) =>
          visibleColumns.map((col) => {
            const val = col.selector(row);
            return val !== undefined && val !== null ? String(val) : "—";
          })
        ),
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [13, 148, 136], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [248, 250, 252] },
      });
      doc.save("tasks.pdf");
      toast.success("Tasks exported to PDF successfully!");
    } catch (err) {
      console.error("PDF export error:", err);
      toast.error("Failed to export PDF.");
    }
  };

  // Print Table
  const printTable = () => {
    if (!filteredData || filteredData.length === 0) {
      toast.info("No task data to print.");
      return;
    }
    const printWindow = window.open("", "", "width=900,height=650");
    if (!printWindow) {
      toast.error("Please allow pop-ups for this site.");
      return;
    }
    const tableContent = `
      <html>
        <head>
          <title>Task List</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
            h2 { color: #0d9488; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; }
            th, td { border: 1px solid #ddd; padding: 8px 12px; text-align: left; font-size: 13px; }
            th { background-color: #f1f5f9; font-weight: bold; }
            tr:nth-child(even) { background-color: #f8fafc; }
          </style>
        </head>
        <body>
          <h2>Task Table Report</h2>
          <table>
            <thead>
              <tr>${visibleColumns.map((col) => `<th>${col.name}</th>`).join("")}</tr>
            </thead>
            <tbody>
              ${filteredData
        .map(
          (row) =>
            `<tr>${visibleColumns
              .map((col) => `<td>${col.selector(row) || "—"}</td>`)
              .join("")}</tr>`
        )
        .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;
    printWindow.document.write(tableContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const customStyles = {
    table: {
      style: { fontFamily: "'Inter', sans-serif" },
    },
    headRow: {
      style: {
        background: "#f1f5f9",
        borderBottom: "2px solid #e2e8f0",
        minHeight: "46px",
      },
    },
    headCells: {
      style: {
        fontSize: "12px",
        fontWeight: "700",
        color: "#475569",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        paddingLeft: "14px",
        paddingRight: "14px",
      },
    },
    rows: {
      style: {
        minHeight: "52px",
        borderBottomColor: "#e2e8f0",
        fontFamily: "'Inter', sans-serif",
        fontSize: "13px",
        transition: "background 0.15s",
      },
      highlightOnHoverStyle: {
        background: "#f0fdfa",
        boxShadow: "inset 3px 0 0 #0d9488",
        transition: "background 0.15s, box-shadow 0.15s",
      },
      stripedStyle: {
        background: "#fafbfe",
      },
    },
    cells: {
      style: {
        paddingLeft: "14px",
        paddingRight: "14px",
      },
    },
    pagination: {
      style: {
        borderTop: "1px solid #e2e8f0",
        background: "#fafbff",
        fontSize: "13px",
        color: "#64748b",
        fontFamily: "'Inter', sans-serif",
        minHeight: "52px",
        paddingLeft: "16px",
        paddingRight: "16px",
      },
    },
    noData: {
      style: {
        padding: "40px 20px",
        color: "#94a3b8",
        fontFamily: "'Inter', sans-serif",
        fontSize: "14px",
      },
    },
  };

  return (
    <div className="lead-table-card">
      {/* ── Card Header ── */}
      <div className="lead-card-header">
        <div className="lead-card-title">
          <div className="lead-card-icon" style={{ background: "#f0fdfa", color: "#0d9488" }}>
            <FaTasks />
          </div>
          <h4>Task Table</h4>
          {filteredData.length > 0 && (
            <span className="lead-card-badge">{filteredData.length} records</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => navigate("/create-task")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            background: "#0d9488",
            color: "#ffffff",
            border: "1px solid #0d9488",
            borderRadius: "6px",
            padding: "6px 14px",
            fontSize: "12.5px",
            fontWeight: "600",
            fontFamily: "var(--font-base)",
            cursor: "pointer",
            height: "34px",
            transition: "all 0.15s ease",
            whiteSpace: "nowrap",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#0f766e";
            e.currentTarget.style.borderColor = "#0f766e";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#0d9488";
            e.currentTarget.style.borderColor = "#0d9488";
          }}
        >
          <FaPlus style={{ fontSize: "11px" }} />
          Create Task
        </button>
      </div>

      {/* ── Toolbar with Action Buttons ── */}
      <div
        className="gai-toolbar"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "12px",
          padding: "12px 20px",
          background: "#ffffff",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <div
          className="gai-toolbar-actions"
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <button
            type="button"
            className="gai-tb-btn"
            onClick={copyToClipboard}
            title="Copy table data"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              background: "#ffffff",
              color: "#475569",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              padding: "6px 12px",
              fontSize: "12.5px",
              fontWeight: 500,
              cursor: "pointer",
              height: "34px",
              transition: "all 0.15s ease",
            }}
          >
            <FaCopy style={{ fontSize: "12px", color: "#0d9488" }} /> Copy
          </button>

          <button
            type="button"
            className="gai-tb-btn"
            onClick={exportCSV}
            title="Export CSV"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              background: "#ffffff",
              color: "#475569",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              padding: "6px 12px",
              fontSize: "12.5px",
              fontWeight: 500,
              cursor: "pointer",
              height: "34px",
              transition: "all 0.15s ease",
            }}
          >
            <FaFileCsv style={{ fontSize: "12px", color: "#0d9488" }} /> CSV
          </button>

          <button
            type="button"
            className="gai-tb-btn"
            onClick={exportPDF}
            title="Export PDF"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              background: "#ffffff",
              color: "#475569",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              padding: "6px 12px",
              fontSize: "12.5px",
              fontWeight: 500,
              cursor: "pointer",
              height: "34px",
              transition: "all 0.15s ease",
            }}
          >
            <FaFilePdf style={{ fontSize: "12px", color: "#dc2626" }} /> PDF
          </button>

          <button
            type="button"
            className="gai-tb-btn"
            onClick={printTable}
            title="Print table"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              background: "#ffffff",
              color: "#475569",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              padding: "6px 12px",
              fontSize: "12.5px",
              fontWeight: 500,
              cursor: "pointer",
              height: "34px",
              transition: "all 0.15s ease",
            }}
          >
            <FaPrint style={{ fontSize: "12px", color: "#64748b" }} /> Print
          </button>

          {/* Column Visibility Dropdown */}
          <OutsideClickHandler onOutsideClick={() => setColDropOpen(false)}>
            <div style={{ position: "relative" }}>
              <button
                type="button"
                className="gai-tb-btn"
                onClick={() => setColDropOpen((prev) => !prev)}
                title="Toggle columns visibility"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  background: "#ffffff",
                  color: "#475569",
                  border: "1px solid #e2e8f0",
                  borderRadius: "6px",
                  padding: "6px 12px",
                  fontSize: "12.5px",
                  fontWeight: 500,
                  cursor: "pointer",
                  height: "34px",
                  transition: "all 0.15s ease",
                }}
              >
                <FaColumns style={{ fontSize: "12px", color: "#0d9488" }} /> Columns
              </button>

              {colDropOpen && (
                <div
                  className="col-vis-dropdown"
                  style={{
                    position: "absolute",
                    top: "calc(100% + 6px)",
                    left: 0,
                    zIndex: 1050,
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
                    padding: "8px",
                    minWidth: "190px",
                  }}
                >
                  {columns.map((col) => {
                    const isVis = !!selectedColumns[col.name];
                    return (
                      <div
                        key={col.name}
                        className="col-vis-item"
                        onClick={() =>
                          setSelectedColumns((prev) => ({
                            ...prev,
                            [col.name]: !prev[col.name],
                          }))
                        }
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "6px 10px",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "13px",
                          color: isVis ? "#1e293b" : "#94a3b8",
                          background: isVis ? "#f0fdfa" : "transparent",
                          marginBottom: "2px",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isVis}
                          onChange={() => { }}
                          style={{ cursor: "pointer", accentColor: "#0d9488" }}
                        />
                        <span>{col.name}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </OutsideClickHandler>
        </div>

        {/* Search Box */}
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            minWidth: "220px",
          }}
        >
          <FaSearch
            style={{
              position: "absolute",
              left: "5px",
              color: "#94a3b8",
              fontSize: "13px",
            }}
          />
          <input
            type="text"
            className="form-control"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="    Search tasks..."
            style={{
              paddingLeft: "32px",
              height: "34px",
              fontSize: "13px",
              borderRadius: "6px",
              border: "1px solid #cbd5e1",
            }}
          />
        </div>
      </div>

      {/* ── Data Table ── */}
      <div style={{ padding: "0 4px" }}>
        <DataTable
          columns={visibleColumns}
          data={filteredData}
          onRowClicked={(row) => {
            const rowIndex = filteredData.findIndex((item) => item.id === row.id);
            if (row?.id) {
              navigate(`/task-details/${row.id}/${rowIndex >= 0 ? rowIndex : 0}`);
            }
          }}
          pointerOnHover
          pagination
          highlightOnHover
          striped
          responsive
          customStyles={customStyles}
          noDataComponent={
            <div style={{ padding: "30px", color: "#64748b", textAlign: "center" }}>
              No tasks found. Click &quot;Create Task&quot; to add a new task.
            </div>
          }
        />
      </div>
    </div>
  );
};

export default TaskDashboardTable;